import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Settings,
  User,
  BookOpen,
  Target,
  Phone,
  MessageCircle,
  Mail,
  Linkedin,
  Calendar,
  Video,
  Loader2,
} from 'lucide-react';
import type { ReadinessItem } from '../../services/readinessService';

interface OutreachReadinessBlockerProps {
  setupItems: ReadinessItem[];
  agentItems: ReadinessItem[];
  connectionItems: ReadinessItem[];
  completedCount: number;
  totalCount: number;
  isFirstTime: boolean;
  onRefresh: () => void;
  refreshing?: boolean;
}

const ICON_MAP: Record<string, React.ElementType> = {
  profile: User,
  about_company: BookOpen,
  icp: Target,
  call_agent: Phone,
  whatsapp_agent: MessageCircle,
  email_agent: Mail,
  linkedin_agent: Linkedin,
  phone: Phone,
  whatsapp: MessageCircle,
  email: Mail,
  linkedin: Linkedin,
  calendar: Calendar,
  video: Video,
};

const GROUP_CONFIG = [
  { key: 'setup', label: 'General Setup', description: 'Profile, sales materials, and ideal customer profile' },
  { key: 'agents', label: 'AI Agents', description: 'Agent identity, scripts, and templates' },
  { key: 'connections', label: 'Connections', description: 'Phone, messaging, email, and calendar integrations' },
] as const;

function ReadinessItemRow({ item }: { item: ReadinessItem }) {
  const navigate = useNavigate();
  const Icon = ICON_MAP[item.key] || Settings;

  return (
    <div className="flex items-center justify-between py-3 px-4 rounded-xl hover:bg-gray-50/80 transition-colors">
      <div className="flex items-center space-x-3 min-w-0">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
          item.ready
            ? 'bg-emerald-50 text-emerald-500'
            : 'bg-[#1875FF]/8 text-[#1875FF]'
        }`}>
          {item.ready ? <Check size={16} strokeWidth={2.5} /> : <Icon size={16} />}
        </div>
        <div className="min-w-0">
          <span className={`text-sm font-medium block ${item.ready ? 'text-gray-400' : 'text-gray-800'}`}>
            {item.label}
          </span>
          {!item.ready && item.missingItems.length > 0 && (
            <p className="text-xs text-gray-400 mt-0.5 truncate">
              Missing: {item.missingItems.join(', ')}
            </p>
          )}
        </div>
      </div>
      {!item.ready && (
        <button
          onClick={() => navigate(`/settings#${item.settingsHash}`)}
          className="text-xs font-semibold text-[#1875FF] hover:text-[#1260cc] ml-3 px-3 py-1.5 rounded-lg hover:bg-[#1875FF]/5 transition-colors whitespace-nowrap shrink-0"
        >
          Set Up
        </button>
      )}
    </div>
  );
}

function GroupSection({
  label,
  description,
  items,
  defaultOpen,
}: {
  label: string;
  description: string;
  items: ReadinessItem[];
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const completedInGroup = items.filter(i => i.ready).length;
  const allDone = completedInGroup === items.length;

  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
            allDone
              ? 'bg-emerald-50 text-emerald-500'
              : 'bg-[#1875FF]/10 text-[#1875FF]'
          }`}>
            {allDone ? <Check size={14} strokeWidth={3} /> : `${completedInGroup}`}
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-gray-900">{label}</p>
            <p className="text-xs text-gray-400">{description}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2.5">
          <span className="text-xs text-gray-400 font-medium">
            {completedInGroup}/{items.length}
          </span>
          {open ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-0.5 border-t border-gray-50">
          {items.map(item => (
            <ReadinessItemRow key={item.key} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function OutreachReadinessBlocker({
  setupItems,
  agentItems,
  connectionItems,
  completedCount,
  totalCount,
  isFirstTime,
  onRefresh,
  refreshing,
}: OutreachReadinessBlockerProps) {
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const groupMap: Record<string, ReadinessItem[]> = {
    setup: setupItems,
    agents: agentItems,
    connections: connectionItems,
  };

  const hasIncompleteSetup = setupItems.some(i => !i.ready);
  const hasIncompleteAgents = agentItems.some(i => !i.ready);

  return (
    <div className="max-w-2xl mx-auto py-10 px-4">
      <div className="text-center mb-8">
        <div className="p-4 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-2xl w-16 h-16 flex items-center justify-center mx-auto mb-5">
          <ShieldCheck className="w-8 h-8 text-[#1875FF]" />
        </div>
        <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight mb-2">
          {isFirstTime ? 'Complete Your Setup to Get Started' : 'Some Items Need Attention'}
        </h2>
        <p className="text-gray-500 max-w-md mx-auto text-sm leading-relaxed">
          {isFirstTime
            ? 'Before requesting leads, make sure your profile, AI agents, and connections are fully configured.'
            : 'A few items need to be resolved before you can request new leads.'}
        </p>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Setup Progress</span>
          <span className="text-xs font-bold text-gray-700">{completedCount} of {totalCount}</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700 ease-out bg-gradient-to-r from-[#1875FF] to-[#05E0D1]"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      <div className="space-y-3 mb-8">
        {GROUP_CONFIG.map(group => (
          <GroupSection
            key={group.key}
            label={group.label}
            description={group.description}
            items={groupMap[group.key]}
            defaultOpen={
              group.key === 'setup' ? hasIncompleteSetup :
              group.key === 'agents' ? hasIncompleteAgents && !hasIncompleteSetup :
              !hasIncompleteSetup && !hasIncompleteAgents
            }
          />
        ))}
      </div>

      <div className="text-center">
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#1875FF]/25 hover:scale-105 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          {refreshing ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              <span>Checking...</span>
            </>
          ) : (
            <span>Re-check Readiness</span>
          )}
        </button>
      </div>
    </div>
  );
}
